import type { Tone } from '../../theme/listkit'

/**
 * Turn one `moderation_actions` row into what the operator needs to see.
 *
 * WHY THIS IS A PURE MODULE. The log renders two families of action that share
 * one table and almost nothing else:
 *
 *   - MEMBER actions (`admin-action`): suspend / stream_ban / … — these carry a
 *     real `target_user_id` and a reason, and the row reads "who, to whom, why".
 *   - FEED actions (`feed-moderate`, `feed-purge`): these target an ACTIVITY or a
 *     DATE RANGE, so `target_user_id` is deliberately null and everything that
 *     identifies the target lives in `metadata`.
 *
 * The page rendered `target_user_id ?? '—'` for every row, so every feed action
 * showed a bare dash: the audit trail recorded a bulk deletion but displayed
 * nothing about what was deleted. `feed-purge` writes `deleted_ids` precisely so
 * a soft purge stays recoverable — and that list was reaching the database and
 * stopping there.
 *
 * Mapping shape to text is the part worth testing, so it lives here rather than
 * inline in JSX.
 */

/** Metadata written by feed-purge (`feed_purge_range`). */
interface PurgeMeta {
  from?: unknown
  to?: unknown
  feed?: unknown
  hard_delete?: unknown
  matched?: unknown
  deleted?: unknown
  failed_chunks?: unknown
  deleted_ids?: unknown
}
/** Metadata written by feed-moderate (`feed_soft_delete` / `feed_hard_delete` / `feed_restore`). */
interface ItemMeta {
  activity_id?: unknown
  text?: unknown
  author?: unknown
}

export interface ActionDescription {
  tone: Tone
  /** One line stating what happened, in the operator's terms. */
  summary: string
  /** Secondary line: the window, the snapshot text, the failure count. */
  detail: string | null
  /** Stream activity ids this action touched — the recovery key for a soft purge. */
  ids: string[]
  /** True when the affected posts can still be brought back. */
  recoverable: boolean
  /** True for feed-family actions, i.e. rows where target_user_id is meaningless. */
  isFeedAction: boolean
}

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null)

const stamp = (v: unknown): string => {
  const s = str(v)
  if (!s) return '?'
  const d = new Date(s)
  return Number.isNaN(d.getTime())
    ? s
    : d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

export function describeAction(
  action: string,
  metadata: unknown,
  targetUserId: string | null,
): ActionDescription {
  const a = String(action ?? '').toLowerCase()
  const meta = (metadata ?? {}) as Record<string, unknown>

  // ── Bulk purge over a date range ──────────────────────────────────────────
  if (a === 'feed_purge_range') {
    const m = meta as PurgeMeta
    const hard = m.hard_delete === true
    const matched = num(m.matched) ?? 0
    const deleted = num(m.deleted) ?? 0
    const failed = num(m.failed_chunks) ?? 0
    const ids = Array.isArray(m.deleted_ids) ? (m.deleted_ids.filter((v) => typeof v === 'string') as string[]) : []

    const bits = [`${stamp(m.from)} → ${stamp(m.to)}`]
    if (str(m.feed)) bits.push(`feed ${str(m.feed)}`)
    // Surface a partial failure here too: the run reports 200 even when chunks
    // fail, so the log is where an operator would later notice the shortfall.
    if (failed > 0) bits.push(`${plural(failed, 'batch')} failed`)
    else if (deleted < matched) bits.push(`${matched - deleted} not deleted`)

    return {
      tone: hard ? 'red' : 'gold',
      summary: hard
        ? `Permanently deleted ${plural(deleted, 'post')} of ${matched} matched`
        : `Removed ${plural(deleted, 'post')} of ${matched} matched`,
      detail: bits.join(' · '),
      ids,
      recoverable: !hard,
      isFeedAction: true,
    }
  }

  // ── Single-activity moderation ────────────────────────────────────────────
  if (a === 'feed_soft_delete' || a === 'feed_hard_delete' || a === 'feed_restore') {
    const m = meta as ItemMeta
    const id = str(m.activity_id)
    const snippet = str(m.text)
    const author = str(m.author)
    const hard = a === 'feed_hard_delete'

    const bits: string[] = []
    if (snippet) bits.push(`“${snippet.length > 140 ? snippet.slice(0, 140) + '…' : snippet}”`)
    if (author) bits.push(`by ${author}`)

    return {
      tone: hard ? 'red' : a === 'feed_restore' ? 'green' : 'gold',
      summary: hard
        ? 'Permanently deleted 1 post'
        : a === 'feed_restore'
          ? 'Restored 1 post'
          : 'Removed 1 post',
      detail: bits.length ? bits.join(' ') : null,
      ids: id ? [id] : [],
      recoverable: a === 'feed_soft_delete',
      isFeedAction: true,
    }
  }

  // ── Member-level integrity actions (unchanged behaviour) ──────────────────
  return {
    tone: a.startsWith('stream') ? 'jaguar' : /suspend|ban/.test(a) ? 'red' : 'neutral',
    summary: targetUserId ?? '—',
    detail: null,
    ids: [],
    recoverable: false,
    isFeedAction: false,
  }
}
